import React, { useState, useRef } from "react";
import axios from "axios";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { modules, formats } from "../../utils/quillModules";
import { API_URL } from "../../utils/Url";
import { Sidebar } from "../sidebar/Sidebar";
import { Header } from "../header/Header";
import { toast } from "react-toastify";
import "./CreateBlogs.css";

const CreateBlogs = () => {
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [metaTitle, setMetaTitle] = useState("");
  const [metaKeyword, setMetaKeyword] = useState("");
  const [metaDescription, setMetaDescription] = useState("");
  const [author, setAuthor] = useState("");
  const [loading, setLoading] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const fileInputRef = useRef(null);
  const token = sessionStorage.getItem("token");

  const resetForm = () => {
    setTitle("");
    setContent("");
    setMetaTitle("");
    setMetaKeyword("");
    setMetaDescription("");
    setAuthor("");
    setImageFile(null);
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleSubmit = async () => {
    if (loading) return;

    if (!token) {
      toast.error("Unauthorized. Please login again.");
      return;
    }

    if (!title || !content || content === "<p><br></p>") {
      toast.error("Title & Description are required");
      return;
    }

    if (!imageFile) {
      toast.warning("Blog image is required");
      return;
    }

    try {
      setLoading(true);

      const formData = new FormData();
      formData.append("title", title.trim());
      formData.append("description", content);
      formData.append("metaTitle", metaTitle);
      formData.append("metaKeyword", metaKeyword);
      formData.append("metaDescription", metaDescription);
      formData.append("authorName", author);
      formData.append("blogImage", imageFile);

      await axios.post(`${API_URL}/api/blog/create_blog`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      toast.success("Blog created successfully ✅");
      resetForm();
    } catch (err) {
      console.error(err.response?.data || err);
      toast.error(err.response?.data?.message || "Something went wrong ❌");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-layout-wrapper">
      {/* Top persistent header */}
      <Header />

      <div className="admin-main-container">
        {/* Navigation Sidebar */}
        <Sidebar />

        {/* Scrollable Main Content Area */}
        <div className="blogs-create-page-content">
          <div className="blogs-create-card">
            <div className="card-header">
              <h2 className="page-title">📝 Create New Post</h2>
              <p className="page-subtitle">
                Write, optimize, and publish your blog post.
              </p>
            </div>

            <div className="form-body-layout">
              {/* Left Side: Main Editor Workspace */}
              <div className="form-section-main">
                <div className="form-group">
                  <label>Blog Title</label>
                  <input
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="Enter an engaging title..."
                    className="custom-input title-input"
                  />
                </div>

                <div className="form-group quill-container">
                  <label>Blog Description</label>
                  <ReactQuill
                    value={content}
                    onChange={setContent}
                    modules={modules}
                    formats={formats}
                    className="quill-editor"
                  />
                </div>
              </div>

              {/* Right Side: Featured Assets & Metadata */}
              <div className="form-section-sidebar">
                <div className="form-group">
                  <label>Featured Image</label>
                  <div className="image-upload-wrapper">
                    <input
                      type="file"
                      accept="image/*"
                      ref={fileInputRef}
                      id="blog-image-input"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        setImageFile(file);
                        if (file) setImagePreview(URL.createObjectURL(file));
                      }}
                      hidden
                    />
                    <label
                      htmlFor="blog-image-input"
                      className="upload-dropzone"
                    >
                      {imagePreview ? (
                        <img
                          src={imagePreview}
                          alt="Preview"
                          className="image-preview"
                        />
                      ) : (
                        <div className="upload-placeholder">
                          <span className="upload-icon">📸</span>
                          <span>Click to upload image</span>
                        </div>
                      )}
                    </label>
                  </div>
                </div>

                <div className="form-group">
                  <label>Author Name</label>
                  <input
                    type="text"
                    value={author}
                    onChange={(e) => setAuthor(e.target.value)}
                    placeholder="Writer's name"
                    className="custom-input"
                  />
                </div>

                <div className="seo-box-container">
                  <h3 className="seo-title">🔍 SEO Meta Tags</h3>

                  <div className="form-group">
                    <label>Meta Title</label>
                    <input
                      value={metaTitle}
                      onChange={(e) => setMetaTitle(e.target.value)}
                      placeholder="SEO Title"
                      className="custom-input"
                    />
                  </div>

                  <div className="form-group">
                    <label>Meta Keywords</label>
                    <input
                      value={metaKeyword}
                      onChange={(e) => setMetaKeyword(e.target.value)}
                      placeholder="react, js, coding"
                      className="custom-input"
                    />
                  </div>

                  <div className="form-group">
                    <label>Meta Description</label>
                    <textarea
                      value={metaDescription}
                      onChange={(e) => setMetaDescription(e.target.value)}
                      placeholder="Short excerpt for search results..."
                      className="custom-input custom-textarea"
                      rows="3"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Bottom Form Submissions */}
            <div className="form-actions-bar">
              <button className="btn-cancel" onClick={resetForm}>
                Discard Draft
              </button>
              <button
                className="btn-submit"
                disabled={loading}
                onClick={handleSubmit}
              >
                {loading ? "Publishing..." : "Publish Blog"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateBlogs;
